import { IPromise, IScope } from "angular";
import { IRestLayerRequest } from "@gtmhub/core";
import { IStateInit } from "@gtmhub/core/routing";
import { IIndicator } from "@gtmhub/error-handling";
import { IGoal } from "@gtmhub/goals/models";
import { GoalService } from "@gtmhub/goals/services/goal.service";
import { ICollection } from "@webapp/core/core.models";
import { getPropertyFilter } from "./utils";

const CONSTANTS = {
  loading: "loading",
  paging: "paging",
  startingPage: 1,
  itemsPerPage: 10,
  approved: "approved",
  inReview: "inReview",
  draft: "draft",
  topLevelDashed: "top-level",
  crossSessionAlignedDashed: "cross-session-aligned",
};

interface IInspectGoalsWorkflowService {
  goalsStatName: string;
  planningSessionId: string;
  page: number;
}

interface IInspectGoalsWorkflowCtrlScope extends IScope {
  indicators: {
    loading?: IIndicator;
    paging?: IIndicator;
  };
  params: IInspectGoalsWorkflowService;
  goals: ICollection<IGoal>;
  currentPage: number;
  totalPages: number;
  itemsPerPage: number;
  onPageChange(page: number): void;
}

export class InspectGoalsWorkflowCtrl implements IStateInit {
  public static $inject = ["$scope", "$stateParams", "GoalService"];

  private workflowGoalsParams: IRestLayerRequest = {
    fields: "id,name,ownerIds,sessionId,workflow{status,reviewersNeeded,reviewers}",
    filter: {
      sessionId: {
        $in: [this.$stateParams.planningSessionId],
      },
    },
    limit: CONSTANTS.itemsPerPage,
  };

  constructor(private $scope: IInspectGoalsWorkflowCtrlScope, private $stateParams: IInspectGoalsWorkflowService, private goalService: GoalService) {}

  public stateInit(): IPromise<unknown> {
    return this.init();
  }

  private init = (): IPromise<unknown> => {
    this.$scope.indicators = {};
    this.$scope.params = { ...this.$stateParams };
    this.$scope.currentPage = CONSTANTS.startingPage;
    this.$scope.itemsPerPage = CONSTANTS.itemsPerPage;
    this.$scope.onPageChange = this.onPageChange;

    this.workflowGoalsParams = this.configureWorkflowGoalsParams(this.workflowGoalsParams, this.$stateParams.goalsStatName);

    return this.getWorkflowGoals();
  };

  private configureWorkflowGoalsParams = (params: IRestLayerRequest, statName: string): IRestLayerRequest => {
    // Filters for parentGoalSessionId, parent.goalId, parent.metricId have to check if prop exists OR if it is an enmpty string.
    // The reason being that when an OKR is created without a parent, these properties will not exist,
    // but when the OKRs parent is removed in the UI, these properties are written in the DB as empty string.
    // That makes the filters more complex, but after this bug is resolved in BE the filters can be simplified.
    // https://quantive-inc.atlassian.net/browse/GVS-32864

    const crossSessionAlignedFilter = getPropertyFilter({
      prop: "parentGoalSessionId",
      exists: true,
      logicalOperator: "$and",
      comparisonOperator: "$nin",
      values: [this.$stateParams.planningSessionId, ""],
    });

    const topLevelFilter = {
      $and: ["parent.goalId", "parent.metricId"].map((parent) =>
        getPropertyFilter({
          prop: parent,
          exists: false,
          logicalOperator: "$or",
          comparisonOperator: "$in",
          values: [""],
        })
      ),
    };

    switch (statName) {
      case CONSTANTS.approved:
        this.workflowGoalsParams.filter["workflow.status"] = { $in: [CONSTANTS.approved] };
        break;

      case CONSTANTS.inReview:
        this.workflowGoalsParams.filter["workflow.status"] = { $in: [CONSTANTS.inReview] };
        break;

      case CONSTANTS.draft:
        this.workflowGoalsParams.filter["workflow.status"] = { $in: [CONSTANTS.draft] };
        break;

      case CONSTANTS.topLevelDashed:
        this.workflowGoalsParams.filter = {
          $and: [topLevelFilter, this.workflowGoalsParams.filter],
        };
        break;

      case CONSTANTS.crossSessionAlignedDashed:
        this.workflowGoalsParams.filter = {
          $and: [crossSessionAlignedFilter, this.workflowGoalsParams.filter],
        };
        break;

      default:
        break;
    }

    return params;
  };

  private getWorkflowGoals = (indicator = CONSTANTS.loading, skip = 0): IPromise<void | { error: unknown }> => {
    this.$scope.indicators[indicator] = { progress: true };

    this.workflowGoalsParams.skip = skip;

    return this.goalService.getGoalsV2(this.workflowGoalsParams).then(
      (goalsResponse) => {
        delete this.$scope.indicators[indicator];

        this.$scope.goals = goalsResponse;
        this.$scope.totalPages = Math.ceil(this.$scope.goals.totalCount / CONSTANTS.itemsPerPage);
      },
      (error) => (this.$scope.indicators[indicator] = { error })
    );
  };

  private onPageChange = (page: number): void => {
    this.$scope.currentPage = page;

    const skip = (page - 1) * CONSTANTS.itemsPerPage;
    this.getWorkflowGoals(CONSTANTS.paging, skip);
  };
}
